import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import DocumentInformation from '@repo/ui/document-information'
import {
  AdultPlusChildIcon,
  BOneFileIcon,
  HealthFormIcon,
  IdIcon,
  IdPhotoIcon,
  ImageIcon,
  TelephoneIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { canAccessEnrollmentHealth } from '../../../api/enrollments/can-access-enrollment-health'
import { getEnrollmentPickupActions } from '../../../api/enrollments/can-modify-enrollment-pickup'
import { enrollmentHasTest } from '../../../api/enrollments/has-test'
import {
  lockChildInfo,
  lockHealthPapers,
  lockIdentityPapers,
  lockIdentityPicture,
  lockLanguageTest,
  lockPickup,
  unlockChildInfo,
  unlockHealthPapers,
  unlockIdentityPapers,
  unlockIdentityPicture,
  unlockLanguageTest,
  unlockPickup
} from '../../../api/enrollments/information-locks'
import { updateEnrollmentClientContext } from '../../../api/enrollments/update-enrollment'
import useAuth from '../../../lib/hooks/use-auth'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { EnrollmentInformationStatus } from '../../../lib/types/enums/enrollment/enrollment-information-status.enum'
import { enrollmentScoreEnumMapping } from '../../../lib/types/enums/enrollment/score.enum'
import { getEnrollmentClientContextJourneyFields } from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { UpdateInput } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import UploadSecureFileInput from '../../elements/inputs/secure-file-input'

const EnrollmentProfileDocuments = () => {
  const { enrollment, setEnrollment } = useEnrollment()
  const { user } = useAuth()
  const { downloadDocuments } = useDownloadDocuments()

  const { data: getEnrollmentPickupActionsData } = useQuery({
    queryKey: ['get-enrollment-pickup-actions', enrollment.id],
    queryFn: () => getEnrollmentPickupActions(enrollment.id),
    enabled: !!enrollment.id
  })

  const { data: canAccessEnrollmentHealthData, isPending: isCanAccessEnrollmentHealthLoading } =
    useQuery({
      queryKey: ['can-access-enrollment-health', enrollment.id],
      queryFn: () => canAccessEnrollmentHealth(enrollment.id),
      enabled: !!enrollment.id
    })

  const { data: enrollmentHasTestData } = useQuery({
    queryKey: ['enrollment-has-test', enrollment.id],
    queryFn: () => {
      if (enrollment.id) return enrollmentHasTest(enrollment.id)
    },
    enabled: !!enrollment.id
  })

  const { mutateAsync } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) =>
      updateEnrollmentClientContext(input, getEnrollmentClientContextJourneyFields(user.role)),
    onError: showAxiosError,
    onSuccess: (data) => {
      toast.success('Profil de l’enfant mis à jour avec succès')
      setEnrollment(data)
    }
  })

  async function handleMedicalPdfClick() {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.enrollments.exportEnrollmentHealthPdf.url, {
          id: enrollment.id
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) window.open(fileLink, '_blank')
  }

  async function handleLanguageTestPdfClick() {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(
          API.enrollments.exportEnrollmentDetailedLanguageTestResultPdf.url,
          { id: enrollment.id }
        )
      },
      { automaticallyDownload: false }
    )

    if (fileLink) window.open(fileLink, '_blank')
  }

  async function handleProfilePdfClick() {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.enrollments.exportEnrollmentProfilePdf.url, {
          id: enrollment.id
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) window.open(fileLink, '_blank')
  }

  return (
    <div className="flex justify-between gap-3">
      <div className="flex-1 flex flex-col gap-3">
        <DocumentInformation
          title="Papiers d’identité"
          icon={IdIcon}
          status={enrollment.childIdentityPapersSubmitted ? 'validated' : 'pending'}
          locked={enrollment.childIdentityPapersSubmitted}
          onLockChange={async (locked) => {
            if (locked) await lockIdentityPapers(enrollment.id)
            else await unlockIdentityPapers(enrollment.id)
            setEnrollment({ ...enrollment, childIdentityPapersSubmitted: locked })
          }}
        />
        {getEnrollmentPickupActionsData?.canView && (
          <DocumentInformation
            title="Infos retour indiv"
            icon={AdultPlusChildIcon}
            status={enrollment.childPickupSubmitted ? 'validated' : 'pending'}
            locked={enrollment.childPickupSubmitted}
            onLockChange={async (locked) => {
              if (locked) await lockPickup(enrollment.id)
              else await unlockPickup(enrollment.id)
              setEnrollment({ ...enrollment, childPickupSubmitted: locked })
            }}
          >
            {enrollment.childPickupSubmitted && enrollment.enrollmentPickup && (
              <div className="flex gap-2">
                <Badge
                  variant="white"
                  text={toFullName(
                    enrollment.enrollmentPickup.firstName,
                    enrollment.enrollmentPickup.lastName,
                    { options: { mode: 'lastname-firstname' } }
                  )}
                />
                <Badge
                  variant="white"
                  text={`Tél : ${formatPhoneNumber(enrollment.enrollmentPickup.phoneNumber)}`}
                  iconPosition="start"
                  icon={<TelephoneIcon className="w-5 h-5" color={tailwindColors.grey.DEFAULT} />}
                />
              </div>
            )}
          </DocumentInformation>
        )}

        <DocumentInformation
          title="Photo d’identité"
          icon={IdPhotoIcon}
          status={enrollment.secureIdentityPicture ? 'validated' : 'pending'}
          locked={enrollment.identityPictureLocked}
          onLockChange={async (locked) => {
            if (locked) await lockIdentityPicture(enrollment.id)
            else await unlockIdentityPicture(enrollment.id)
            setEnrollment({ ...enrollment, identityPictureLocked: locked })
          }}
        >
          <UploadSecureFileInput
            previewOnClick
            placeholder={'Glisser-déposer ou parcourir'}
            types={[FileTypeEnum.IMAGE]}
            icon={ImageIcon}
            files={
              enrollment.secureIdentityPicture ? [enrollment.secureIdentityPicture] : undefined
            }
            onFilesChange={(remaining) => {
              if (remaining.length === 0)
                mutateAsync({ id: enrollment.id, secureIdentityPicture: null })
            }}
            onNewFiles={(newFiles) => {
              mutateAsync({ id: enrollment.id, secureIdentityPicture: { file: newFiles[0] } })
            }}
          />
        </DocumentInformation>
      </div>
      <div className="flex-1 flex flex-col gap-3">
        {!isCanAccessEnrollmentHealthLoading && canAccessEnrollmentHealthData && (
          <DocumentInformation
            title="Fiche sanitaire de liaison"
            icon={HealthFormIcon}
            status={enrollment.childHealthPapersSubmitted ? 'validated' : 'pending'}
            locked={enrollment.childHealthPapersSubmitted}
            onLockChange={async (locked) => {
              if (locked) await lockHealthPapers(enrollment.id)
              else await unlockHealthPapers(enrollment.id)
              setEnrollment({ ...enrollment, childHealthPapersSubmitted: locked })
            }}
          >
            <DocumentInformation.PdfDocument
              text="Voir le formulaire"
              onClick={async () => {
                if (enrollment.childHealthPapersSubmitted) await handleMedicalPdfClick()
              }}
              status={enrollment.childHealthPapersSubmitted ? 'validated' : 'pending'}
            />
          </DocumentInformation>
        )}

        {enrollmentHasTestData && (
          <DocumentInformation
            title="Test de niveau"
            icon={BOneFileIcon}
            status={!!enrollment?.languageTestSubmission?.submittedAt ? 'validated' : 'pending'}
            locked={enrollment?.languageTestSubmissionLocked}
            onLockChange={async (locked) => {
              if (locked) await lockLanguageTest(enrollment.id)
              else await unlockLanguageTest(enrollment.id)
              setEnrollment({ ...enrollment, languageTestSubmissionLocked: locked })
            }}
          >
            {enrollment?.languageTestSubmission && (
              <DocumentInformation.PdfDocument
                text={
                  enrollment?.languageTestSubmission ? 'Voir les résultats du test' : undefined
                }
                status={enrollment?.languageTestSubmissionLocked ? 'validated' : 'pending'}
                onClick={async () => {
                  if (enrollment?.languageTestSubmission) await handleLanguageTestPdfClick()
                }}
              >
                <div className="w-fit flex flex-row gap-2">
                  {!!enrollment?.languageTestSubmission?.level && (
                    <Badge
                      variant="blue"
                      text={`Niveau : ${enrollmentScoreEnumMapping[enrollment?.languageTestSubmission.level].code}`}
                      size="medium"
                      className="w-fit"
                    />
                  )}

                  {!!enrollment?.languageTestSubmission?.score && (
                    <Badge
                      variant="blue"
                      text={`${enrollment?.languageTestSubmission.score}/100`}
                      size="medium"
                      className="w-fit"
                    />
                  )}
                </div>
              </DocumentInformation.PdfDocument>
            )}
          </DocumentInformation>
        )}

        <DocumentInformation
          title="Profil du jeune"
          icon={UserTwoIcon}
          status={
            enrollment.childInfoStatus === EnrollmentInformationStatus.COMPLETED
              ? 'validated'
              : enrollment.childInfoStatus === EnrollmentInformationStatus.TO_BE_COMPLETED
                ? 'to-be-validated'
                : 'pending'
          }
          locked={enrollment.childInfoStatus !== EnrollmentInformationStatus.PENDING}
          onLockChange={async (locked) => {
            if (locked) await lockChildInfo(enrollment.id)
            else await unlockChildInfo(enrollment.id)
            setEnrollment({
              ...enrollment,
              childInfoStatus: locked
                ? EnrollmentInformationStatus.TO_BE_COMPLETED
                : EnrollmentInformationStatus.PENDING
            })
          }}
        >
          <DocumentInformation.PdfDocument
            text="Voir le PDF"
            onClick={async () => {
              await handleProfilePdfClick()
            }}
            status={'pending'}
          />
        </DocumentInformation>
      </div>
    </div>
  )
}

export default EnrollmentProfileDocuments
